import axios, { Axios } from 'axios';
import { isEmpty } from 'lodash';
import { getConfig } from 'config';
import store from 'store';
import { authStateSelector, authTokenSelector } from 'store/selectors/auth-selectors';
import logError from 'utils/log-error';
import { logoutAction } from 'store/actions/auth-actions';
import { MFA_ENDPOINT_PREFIX } from 'constants/endpoints';
import { MFA_ERROR_KEY_PREFIX } from 'constants/mfa';

let instance: Axios = null;

export const initApi = (): void => {
  instance = axios.create({
    baseURL: getConfig('API_ENDPOINT'),
  });

  instance.interceptors.request.use(config => {
    const newConfig = { ...config };
    newConfig.headers = newConfig.headers ?? {};

    // Base URL of the currently running React app for neembu admin level authentication
    newConfig.headers['X-Neembu-Agency-Slug'] = window.location.hostname;
    if (authStateSelector(store.getState())) {
      const token = authTokenSelector(store.getState());
      newConfig.headers.Authorization = `Bearer ${token}`;
    }
    return newConfig;
  });

  instance.interceptors.response.use(
    res => {
      return res;
    },
    error => {
      // Kiosk endpoints own their 401s (revoked session -> exit kiosk), and must NOT trigger the
      // global "session expired -> logout/redirect" handler, which would break the locked shell.
      // KioskPin/Reveal also 401s on a plain wrong-password retry, which callers handle inline.
      const requestUrl = error?.config?.url ?? '';
      const isKioskRequest = requestUrl.includes('KioskSession') || requestUrl.includes('KioskPin');
      // MFA runs pre-auth: there is no session to end, so this handler's redirect is not a logout, it
      // is a hard reload that throws away whatever the user was standing on. Enrollment and the
      // returning-user challenge both handle their own 401s on-screen.
      //
      // Recognised two ways, and both are needed. By URL, for the Auth/Mfa endpoints themselves. By
      // error key, because Auth/login can also answer [MFA] SESSION_EXPIRED — when an enrollment is
      // removed between the password check and the challenge opening — and a hard redirect there
      // would wipe the store before the login form could explain what happened.
      const isMfaRequest = requestUrl.includes(MFA_ENDPOINT_PREFIX);
      const isMfaError = `${error?.response?.data?.error ?? ''}`.startsWith(MFA_ERROR_KEY_PREFIX);
      if (error?.response?.status === 401 && !isKioskRequest && !isMfaRequest && !isMfaError) {
        store.dispatch(logoutAction(true));
      }

      if (error?.response?.status === 500) {
        logError(error);
      }
      return Promise.reject(error);
    },
  );
};

export const getApi = (): Axios => {
  if (isEmpty(instance)) {
    initApi();
  }
  return instance;
};
